// Static content for the Karamjeet Sodhi personal website.
// CMS-managed content (testimonials, gallery, blog) is fetched from the API.

export const IDENTITIES = [
  "Educator",
  "Author",
  "Trainer",
  "Innovator",
  "Consultant",
];

export const STATS = [
  { value: "1,000+", label: "Trainers Trained" },
  { value: "10,000+", label: "Teachers Trained" },
  { value: "1,000,000+", label: "Students Impacted" },
  { value: "2 Decades", label: "Of Impact" },
];

export const ABOUT = {
  intro:
    "Karamjeet Singh Sodhi is an accomplished and internationally recognised educator-trainer, with 28 years of K-12 experience.",
  body: [
    "His work bridges classroom realities with global educational research, deeply grounded in Indian contexts and aligned with the National Education Policy (NEP 2020), the National Curriculum Framework for School Education (NCF-SE 2023) and the demands of 21st-century learning.",
    "Operating as an independent trainer, framework architect and curriculum developer alongside his role as Academic Excellence Coordinator at CM SHRI School, Amalwas Jawalapuri, Karamjeet has — across two decades — trained 1,000+ trainers and 10,000+ teachers, with a ripple effect reaching over a million students.",
    "Karamjeet is a TESOL International Faculty Member, CBSE Master Trainer, US State Department Virtual Exchange Alumni and a sought-after keynote speaker at national and international forums.",
  ],
  highlights: [
    "TESOL International Faculty Member",
    "CBSE TOT & Master Trainer · KRP · MCD Mentor Trainer",
    "Core Committee Member — CBSE English (Secondary Classes) 2026–29",
    "US Embassy — RELO decade-long partnership",
    "Singapore NIE 2017 — first batch of Delhi Govt teachers sent abroad",
    "SUSI 2026 Educator — University of Montana",
    "Selected International Virtual Presenter — ASCD & ISTE Live 2026",
    "US State Department Virtual Exchange Alumni — Certificate of High Performer",
  ],
};

export const ACTIVITIES = [
  {
    code: "01",
    name: "3B Name Tags",
    tag: "Introductory · Setting the Ground",
    description:
      "An excellent introductory activity that sets the ground for the day — works exceptionally well in classrooms as well as in training rooms. Three Bs, one tag, and a room of strangers instantly becomes a room of names, stories and shared intent.",
    accent: "#0EA5A0",
  },
  {
    code: "02",
    name: "Find Someone Who",
    tag: "Ice Breaker · Energizer",
    description:
      "A high-energy classroom opener that breaks the ice instantly — gets every learner moving, talking and connecting in minutes.",
    accent: "#EF4444",
  },
  {
    code: "03",
    name: "MGWOS",
    tag: "Meet · Greet · Write on Sheet",
    description:
      "A 'get-to-know-each-other' activity that simultaneously sharpens listening and speaking skills — perfect for orientation days, induction trainings and new cohorts.",
    accent: "#D97706",
  },
  {
    code: "04",
    name: "Rainbow Vocabulary",
    tag: "Kinesthetic Vocabulary Game",
    description:
      "A super-kinesthetic vocabulary game built around rotating learning stations — students move through colour-coded zones, building rich word banks through movement.",
    accent: "#EC4899",
  },
  {
    code: "05",
    name: "Let's Bloom",
    tag: "Socratic Board Game · Bloom's Taxonomy",
    description:
      "A Socratic board game grounded in Bloom's Taxonomy — converts dry revision into a structured journey from recall to creation across the levels.",
    accent: "#10B981",
  },
  {
    code: "06",
    name: "Qurious Quest",
    tag: "Socratic Board Game · 5W1H",
    description:
      "A Socratic board game built on the foundational 5W1H questioning technique — trains learners (and teachers) to ask better questions, deeper questions.",
    accent: "#3B82F6",
  },
  {
    code: "07",
    name: "Time Crafter",
    tag: "Productivity System · Ancient Indian Wisdom",
    description:
      "A time-management system rooted in ancient Indian wisdom — a framework for students and adults to author their day with intention, not impulse.",
    accent: "#7C3AED",
  },
  {
    code: "08",
    name: "I Matter",
    tag: "Skill & Productivity Journal · Grades 6–12",
    description:
      "A skill and productivity journal designed for students in Grades 6–12 — develops collaboration, self-leadership and sensitivity to Intellectual Property Rights (IPR).",
    accent: "#1E1B4B",
  },
];

export const PARTNERS = [
  { name: "TESOL International", tag: "Faculty" },
  { name: "RELO · US Embassy", tag: "Decade-long partnership" },
  { name: "CBSE", tag: "Master Trainer · Core Committee" },
  { name: "NCERT", tag: "Curriculum contributor" },
  { name: "SCERT Delhi", tag: "Mentor Trainer" },
  { name: "Directorate of Education, Delhi", tag: "Academic Excellence Coordinator" },
  { name: "U Educate", tag: "Author" },
  { name: "Microsoft", tag: "Educator program" },
  { name: "Google", tag: "Certified Educator" },
  { name: "Shri Ram Global School", tag: "Training partner" },
  { name: "Modern School", tag: "Training partner" },
  { name: "Heritage School", tag: "Training partner" },
  { name: "Seth Anandram Jaipuria", tag: "Training partner" },
  { name: "Span Consulting", tag: "Consulting partner" },
  { name: "Learning Link Foundation", tag: "Capacity building" },
];

export const FRAMEWORKS = [
  {
    code: "01",
    name: "The 6T Framework",
    tag: "Pedagogy · Original Framework",
    description:
      "An original, classroom-tested framework re-imagining how teachers design learning experiences across six interlocking dimensions — refined across thousands of teachers in India and abroad.",
  },
  {
    code: "02",
    name: "HEART Framework",
    tag: "Teacher Wellbeing & Resilience",
    description:
      "A holistic framework supporting educator wellbeing — Health, Empathy, Awareness, Resilience, Trust — designed for Indian school ecosystems navigating reform fatigue.",
  },
  {
    code: "03",
    name: "21CEF",
    tag: "21st-Century Educator Framework",
    description:
      "A comprehensive competency map aligning teacher capability with NEP 2020 and NCF-SE 2023 expectations across critical thinking, digital fluency, SEL and assessment literacy.",
  },
  {
    code: "04",
    name: "Socratic Hexagons",
    tag: "Inquiry-Based Pedagogy",
    description:
      "A six-sided questioning protocol that scaffolds deep classroom dialogue — moving learners from recall to evaluation through structured Socratic moves.",
  },
  {
    code: "05",
    name: "Smiling Chalk",
    tag: "Joyful Classrooms · Co-authored",
    description:
      "A teacher-facing framework reframing chalk-and-talk into joyful, learner-centred practice — co-authored and used in teacher development workshops nationwide.",
  },
];

export const TRAINING_TOPICS = [
  {
    category: "Signature Trainings · K-12 & Education",
    items: [
      "English Language & Literature — Secondary & Senior Secondary",
      "AI for Educators",
      "Storytelling as Pedagogy",
      "TEACH Framework — Stress Management for Teachers",
      "HEART Framework — Happy Classrooms",
      "Active Learning Strategies",
      "Gamification in the Classroom",
      "Lesson Planning that Works",
      "TESOL's 6 Guiding Principles of Exemplary Teaching",
      "Design Thinking for the Entrepreneurial Mindset",
      "Train the Trainer",
      "Experiential Learning",
      "NEP 2020 & NCF-SE in Context",
      "21st-Century Exemplary Classrooms",
    ],
  },
  {
    category: "Beyond the Classroom · Talks & Sessions",
    items: [
      "The Momentum of Life — Create & Sustain",
      "Ask and It Is Given",
      "The Cosmos is in You",
      "Going That Extra Mile",
      "Turning the Stones",
      "The Science of Asking",
      "Time Management through Time Crafter",
      "Be Exemplary",
      "Science and Spirituality — Gyanam Suvigyanam",
      "4444 Formula for Life",
    ],
  },
];

export const BOOKS = [
  {
    title: "The 6T Framework",
    status: "In development · TESOL Press / Routledge",
    description:
      "A full-length practitioner book expanding the 6T Framework into a comprehensive guide for K-12 educators worldwide.",
    cover: null,
    accent: "#D97706",
  },
  {
    title: "Featured in TESOL Press",
    status: "Contributor",
    description:
      "Karamjeet is a featured contributor in TESOL Press publications, with chapters and articles read by educators across continents.",
    cover: null,
    accent: "#1E1B4B",
  },
  {
    title: "Smiling Chalk",
    status: "Co-authored framework",
    description:
      "A teacher-facing framework reframing classroom practice toward joyful, learner-centred pedagogy.",
    cover: null,
    accent: "#EC4899",
  },
];

export const EVENTS = [
  {
    date: "May–Jul 2026",
    title: "SUSI 2026 Educator — University of Montana, USA",
    detail: "Representing India at the Study of US Institutes programme.",
  },
  {
    date: "2026",
    title: "ASCD & ISTE Live 2026 — Virtual Presenter",
    detail: "Selected international virtual presenter at ASCD & ISTE Live 2026.",
  },
  {
    date: "2025",
    title: "TESOL25 International Convention & Expo",
    detail: "Presenter — TESOL International Convention.",
  },
  {
    date: "2024",
    title: "17+ National Workshops for CBSE",
    detail: "Across India — TOT and capacity-building programmes.",
  },
  {
    date: "Ongoing",
    title: "TESOL International Faculty",
    detail: "Faculty member contributing to TESOL conventions and publications.",
  },
  {
    date: "2017",
    title: "Singapore — NIE Capacity Building",
    detail:
      "Part of the first batch of Delhi Government teachers sent abroad for capacity building.",
  },
  {
    date: "Decade-long",
    title: "US Embassy — RELO Partnership",
    detail: "Longstanding partnership in English language education.",
  },
];

export const CONTACT = {
  email: "connect@karamsodhi.com",
  city: "New Delhi, India",
  linkedin: "https://www.linkedin.com/in/karamsodhi/",
  instagram: "https://instagram.com/",
  facebook: "https://facebook.com/",
};

export const INQUIRY_TYPES = [
  "Speaking / Keynote",
  "Teacher Training",
  "Consulting",
  "Curriculum Design",
  "Media / Interview",
  "General",
];

// Hero portrait — actual TESOL25 photo uploaded by Karamjeet
export const HERO_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_karam-journey/artifacts/29yu7bam_487510556_9938298296227939_284123102985128968_n.jpg";

// Default seed gallery (shown if admin hasn't added any images yet).
// SUSI — Study of the U.S. Institutes fellowship
export const SUSI = {
  program: "Study of the U.S. Institutes (SUSI) — Secondary Educators 2026",
  host: "The Maureen & Mike Mansfield Center, University of Montana, Missoula",
  sponsor: "Sponsored by the U.S. Department of State · RELO / U.S. Embassy",
  intro:
    "Selected to represent India in an intensive U.S. Department of State fellowship on civic education, pedagogy and cultural exchange — five weeks of classrooms, communities, policy corridors and conversations that reframed what a teacher's classroom can hold.",
  highlights: [
    {
      title: "Representing India",
      body:
        "One of a small international cohort of secondary educators chosen for the institute, carrying Indian classroom practice into an American academic setting.",
    },
    {
      title: "Know America",
      body:
        "Designed and presented 'KNOW AMERICA — A SUSI-Inspired Cultural Learning Repository', turning a fellowship experience into a lasting, reusable educational resource.",
    },
    {
      title: "Facilitating the Cohort",
      body:
        "Led sessions on community, mutuality and civic education — the questions of what community we want to build, and who builds it.",
    },
    {
      title: "Policy Corridors",
      body:
        "Study visits into American civic life, including a U.S. Senator's office in Washington, D.C., and institutions where policy meets people.",
    },
  ],
  photos: [
    {
      url:
        "https://customer-assets-jt897jd0.emergentagent.net/job_karam-journey/artifacts/duhiutk7_20260626_222915-4cdc.webp",
      caption: "Presenting 'Know America'",
    },
    {
      url:
        "https://customer-assets-jt897jd0.emergentagent.net/job_karam-journey/artifacts/4xjqfcnx_20260601_172032-4e88.webp",
      caption: "Homage at the Gandhi bust, Mansfield Center",
    },
    {
      url:
        "https://customer-assets-jt897jd0.emergentagent.net/job_karam-journey/artifacts/8o7w64gh_20260613_182230-aa88%20%281%29.webp",
      caption: "U.S. Senator's office, Washington D.C.",
    },
    {
      url:
        "https://customer-assets-jt897jd0.emergentagent.net/job_karam-journey/artifacts/ddrt269c_20260630_200304-3617.webp",
      caption: "Peer roundtable with the SUSI cohort",
    },
  ],
};

export const SEED_GALLERY = [
  {
    image_url:
      "https://customer-assets-jt897jd0.emergentagent.net/job_karam-journey/artifacts/4xjqfcnx_20260601_172032-4e88.webp",
    caption: "Homage to Mahatma Gandhi — Mansfield Center, University of Montana",
  },
  {
    image_url:
      "https://customer-assets-jt897jd0.emergentagent.net/job_karam-journey/artifacts/duhiutk7_20260626_222915-4cdc.webp",
    caption: "Presenting 'Know America' — SUSI Cultural Learning Repository",
  },
  {
    image_url:
      "https://customer-assets-jt897jd0.emergentagent.net/job_karam-journey/artifacts/pghq9ulx_20260602_132829-7f0b.webp",
    caption: "Facilitating a session on community & mutuality — SUSI Secondary Educators 2026",
  },
  {
    image_url:
      "https://customer-assets-jt897jd0.emergentagent.net/job_karam-journey/artifacts/8o7w64gh_20260613_182230-aa88%20%281%29.webp",
    caption: "At the U.S. Senator's office, Washington D.C.",
  },
  {
    image_url:
      "https://customer-assets-jt897jd0.emergentagent.net/job_karam-journey/artifacts/ddrt269c_20260630_200304-3617.webp",
    caption: "Peer roundtable with SUSI cohort — Mansfield Center",
  },
  {
    image_url: HERO_PORTRAIT,
    caption: "TESOL25 International Convention & Expo",
  },
  {
    image_url:
      "https://images.unsplash.com/photo-1776512377708-6b4eb702505e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    caption: "Teacher Training Workshop",
  },
  {
    image_url:
      "https://images.unsplash.com/photo-1629839773973-656704f27e56?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    caption: "Author at Work",
  },
];

// Default seed testimonials — extracted from real participant feedback (Padlet boards)
export const SEED_TESTIMONIALS = [
  {
    id: "seed-1",
    quote:
      "The Offline Storytelling CBP was truly inspiring and engaging. The resource persons beautifully demonstrated how storytelling can transform a simple lesson into a magical learning experience for young minds.",
    name: "Geetanjali Sahani",
    role: "Educator",
    organization: "CBSE Workshop · St. Xavier's",
  },
  {
    id: "seed-2",
    quote:
      "It was a great, knowledgeable and effective session on Facilitation Skills. Hats off to your energy level and confidence — looking forward to many more sessions like this.",
    name: "Participant",
    role: "Facilitation Skills Session",
    organization: "Delhi Govt. Induction Training",
  },
  {
    id: "seed-3",
    quote:
      "Very informative session with Mr. Karamjeet Sodhi who helped us learn the art of storytelling and use it as a pedagogy in classrooms to teach topics to students.",
    name: "Ritu Vaid",
    role: "Teacher",
    organization: "St. Xavier's Workshop",
  },
  {
    id: "seed-4",
    quote:
      "Karamjeet sir was very calm and knowledgeable. Amazing experiences shared by sir — energetic, enthusiastic, and the course has truly benefitted my role in work.",
    name: "Ankur Verma",
    role: "TGT Natural Science",
    organization: "GBSSS Parshant Vihar",
  },
  {
    id: "seed-5",
    quote:
      "Karamjeet sir's lecture was so attractive. Innovative ideas and creative ideas were used — thoroughly enjoyed every minute.",
    name: "Participant",
    role: "Facilitation Skills Session",
    organization: "Delhi Govt. Training",
  },
];
